import { createContext, useContext, useState, useRef, useEffect } from "react";
import { mediaStore } from "./mediaStore";
// ─────────────────────────────────────────────
// SCHEMA (what one question looks like)
// ─────────────────────────────────────────────
//
// QuestionBlock {
//   id       : string
//   items    : MediaItem[]
//   solution : Solution
// }
//
// MediaItem {
//   id    : string
//   ind   : number
//   media : "video" | "image" | "text"
//   res   : { url, local, mcq = 1 }   ← for video/image
//           { text, size, math, mcq = 2 }   ← for text
// }
//
// Solution {
//   hint    : string
// }
// ─────────────────────────────────────────────

const createQuestion = (ind) => ({
  ind,
  items: [],
  solution: {
    hint: "",
  },
});

const QuestionContext = createContext(null);

export function QuestionStoreProvider({ children}) {
  // In QuestionStoreProvider, change initial state:
  const [questions, setQuestions] = useState(() => {
    try {
      const saved = sessionStorage.getItem('draftQuestions');
      if (saved) {
        const parsed = JSON.parse(saved);
        return parsed.map(q => ({
          ...q,
          items: q.items.map(item => {
            if (item.res?.url?.startsWith('__media:')) {
              const key = item.res.url.replace('__media:', '');
              const realUrl = mediaStore.get(key);
              if (realUrl) return { ...item, res: { ...item.res, url: realUrl } };
              // media lost (tab was closed etc) — drop the item
              return null;
            }
            return item;
          }).filter(Boolean),
        }));
      }
    } catch { }
    return [{ ind: 0, items: [], solution: { hint: "" } }];
  });

  const [selectedItem, setSelectedItem] = useState(0);
  const selectedItemRef = useRef(0);
  selectedItemRef.current = selectedItem;// In QuestionStoreProvider, add this useEffect:

  useEffect(() => {
    try {
      const stripped = questions.map(q => ({
        ...q,
        items: q.items.map(item => {
          if (item.res?.local === true) {
            // store the actual data in memory map
            const key = `q${q.ind}_i${item.ind}`;
            mediaStore.set(key, item.res.url);
            // save only the key reference, not the blob
            return { ...item, res: { ...item.res, url: `__media:${key}` } };
          }
          return item;
        }),
      }));
      sessionStorage.setItem('draftQuestions', JSON.stringify(stripped));
    } catch (e) {
      console.warn('sessionStorage save failed:', e);
    }
  }, [questions]);


  // go to next question — stops at the last one
  const nextQuestion = () => {
    setSelectedItem(prev => {
      const lastIndex = questions.length - 1;
      if (prev >= lastIndex) return prev;  // already at end, do nothing
      return prev + 1;
    });
  };

  // go to previous question — stops at 0
  const prevQuestion = () => {
    setSelectedItem(prev => {
      if (prev <= 0) return prev;          // already at start, do nothing
      return prev - 1;
    });
  };

  const addQuestion = () => {
    const lastQuestion = questions[questions.length - 1];

    if (lastQuestion.items.length !== 0) {
      const newQuestion = createQuestion(questions.length); // ind = current length
      setQuestions(prev => [...prev, newQuestion]);         // add to list
      setSelectedItem(questions.length);                   // jump to it
    }
  };

  const getCurrentQuestion = () => {
    const current = selectedItemRef.current;
    return questions[current];
  };

  const addItemToCurrentQuestion = (itemObject) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        const nextInd = q.items.length;           // how many already = index of new one
        return {
          ...q,
          items: [...q.items, { ...itemObject, ind: nextInd }],  // stamp ind onto object
        };
      })
    );
  };

  const deleteItemFromCurrentQuestion = (itemInd) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        const filtered = q.items.filter(it => it.ind !== itemInd);  // remove the item
        const reindexed = filtered.map((it, i) => ({ ...it, ind: i })); // fix all inds
        return { ...q, items: reindexed };
      })
    );

  };

  const editTextItem = (itemInd, newText) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        return {
          ...q,
          items: q.items.map(it => {
            if (it.ind !== itemInd) return it;
            if (it.media !== "text") return it;        // verify media type
            return { ...it, res: { ...it.res, text: newText } };
          }),
        };
      })
    );
  };

  const editTextSize = (itemInd, newSize) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        return {
          ...q,
          items: q.items.map(it => {
            if (it.ind !== itemInd) return it;
            if (it.media !== "text") return it;        // verify media type
            return { ...it, res: { ...it.res, size: newSize } };
          }),
        };
      })
    );
  };

  const getAllData = () => {
    return {
      questions,
      totalQuestions: questions.length,
    };
  };

  const setQuestionTime = (enabled, seconds = 180) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        if (!enabled) {
          const { time, ...rest } = q;   // remove time field
          return rest;
        }
        return { ...q, time: seconds };
      })
    );
  };

  const setQuestionHint = (hint) => {
    const current = selectedItemRef.current;
    setQuestions(prev =>
      prev.map(q => {
        if (q.ind !== current) return q;
        return { ...q, solution: { ...q.solution, hint } };
      })
    );
  };

  const discardDraft = () => {
    sessionStorage.removeItem('draftQuestions');
    mediaStore.clear();
    setQuestions([{ ind: 0, items: [], solution: { hint: "" } }]);
    setSelectedItem(0);
  };
  return (
    <QuestionContext.Provider value={{
      questions, setQuestions, selectedItem, addQuestion,
      addItemToCurrentQuestion, deleteItemFromCurrentQuestion,
      getCurrentQuestion, nextQuestion, prevQuestion,
      editTextItem, editTextSize, getAllData,
      setQuestionTime, setQuestionHint,
      discardDraft
    }}>
      {children}
    </QuestionContext.Provider>
  );
}

export function useQuestions() {
  return useContext(QuestionContext);
}